// Pointer drag/resize pro plovoucí okna (a dialogy v ploše).
//   - startDrag: táhne CELOU floating skupinu (snapshot rectů, posun o dx/dy); dialog v ploše jen sebe
//   - startResize: mění velikost jednoho okna (8 směrů)
// Gesto běží přes CAPTURE OVERLAY (viz dragCaptureOverlay) — fixní průhledný div nad celým
// viewportem odchytává pointer eventy, takže kurzor nevjede do legacy iframu a tažení neseká.
// Zápis jde jen do WM storu, throttle přes requestAnimationFrame; finální rect se clampne do plochy.

import { useCallback } from 'react';

import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';

import { detectSnapZone, isEdgeSnapEnabled, snapRectForZone, type DeskRect } from '../core/snap';
import { useSnapStore } from '../core/snapStore';
import { useWindowManagerStore } from '../core/windowManagerStore';
import { clampRectKeepTitle, type WindowRect } from '../core/windowManagerTypes';
import { writeWindowSizeMemory } from '../core/windowSizeMemory';
import { beginDragCapture } from './dragCaptureOverlay';

const MIN_W = 360;
const MIN_H = 220;
/** Délka „dock" spring animace po snapu (ms) — drží krok s `ANIM_DUR_MS` v useWindowingActions. */
const SNAP_ANIM_MS = 260;

export type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export function usePointerDrag() {
  const { kernel } = useNavigationContext();
  const wm = useWindowManagerStore;

  const startDrag = useCallback(
    (e: React.PointerEvent, layerId: string) => {
      const layer = kernel.getLayer(layerId);
      if (!layer) return;
      e.preventDefault();
      e.stopPropagation();

      const store = wm.getState();
      // Floating skupina → táhne se celá (snapshot všech členů stacku). Dialog v ploše (bez stackId)
      // → táhne se jen on. Docked okno bez rectu (plná plocha / list-detail) → snapshot prázdný → no-op.
      const memberIds = layer.stackId
        ? kernel
            .getState()
            .layers.filter((l) => l.stackId === layer.stackId)
            .map((l) => l.layerId)
        : [layerId];
      const snapshot = memberIds
        .map((id) => ({ id, rect: store.windows[id]?.rect }))
        .filter((m): m is { id: string; rect: WindowRect } => m.rect !== undefined);
      if (snapshot.length === 0) return;

      // Edge-snap jen pro floating skupiny (stackId) a jen když je zapnutý. Plochu (offset+rozměry)
      // si zachytíme jednou na začátku — během tažení se nehýbe. Dialogy v ploše (bez stackId) snap
      // nepoužívají.
      const deskBox =
        layer.stackId !== undefined && isEdgeSnapEnabled()
          ? document.querySelector('.pwm-desktop')?.getBoundingClientRect()
          : undefined;
      const deskRect: DeskRect | null = deskBox
        ? { left: deskBox.left, top: deskBox.top, width: deskBox.width, height: deskBox.height }
        : null;

      const startX = e.clientX;
      const startY = e.clientY;
      let raf = 0;
      document.body.style.userSelect = 'none';

      beginDragCapture(e.pointerId, 'move', {
        onMove: (ev) => {
          const dx = ev.clientX - startX;
          const dy = ev.clientY - startY;
          // Náhled snap zóny z aktuální pozice kurzoru (okno přitom dál sleduje kurzor níže).
          if (deskRect) {
            useSnapStore.getState().setZone(detectSnapZone(ev.clientX, ev.clientY, deskRect));
          }
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = 0;
            wm.getState().setRects(
              snapshot.map(({ id, rect }) => [id, { ...rect, x: rect.x + dx, y: rect.y + dy }])
            );
          });
        },
        onEnd: () => {
          document.body.style.userSelect = '';
          if (raf) cancelAnimationFrame(raf);
          const s = wm.getState();
          const dim = s.desktopSize;

          // Edge-snap: skončil-li kurzor v zóně, přichyť CELOU skupinu na polovinu/čtvrtinu (offset 0
          // = plné překrytí) a zahraj „dock" spring. Náhled vždy schovej.
          const zone = deskRect ? useSnapStore.getState().zone : null;
          useSnapStore.getState().setZone(null);
          if (zone) {
            const base = snapRectForZone(zone, dim);
            const ids = snapshot.map((m) => m.id);
            s.setRects(ids.map((id) => [id, base] as const));
            s.setAnim(ids, 'dock');
            window.setTimeout(() => wm.getState().setAnim(ids, null), SNAP_ANIM_MS);
            return;
          }

          // Okno smí přesahovat ven, ale ne celé zmizet → drž viditelný titulek (clampKeepTitle).
          const fixes = snapshot
            .map(({ id }) => {
              const cur = s.windows[id]?.rect;
              return cur ? ([id, clampRectKeepTitle(cur, dim)] as const) : null;
            })
            .filter((x): x is readonly [string, WindowRect] => x !== null);
          if (fixes.length > 0) s.setRects(fixes);
        },
      });
    },
    [kernel, wm]
  );

  const startResize = useCallback(
    (e: React.PointerEvent, layerId: string, dir: ResizeDir) => {
      const layer = kernel.getLayer(layerId);
      const store = wm.getState();
      const r0 = store.windows[layerId]?.rect;
      if (!r0) return;
      e.preventDefault();
      e.stopPropagation();

      // Okno ve floating skupině (víc členů, plně překrytých – offset 0) se resizuje CELÉ:
      // všichni členové stacku dostanou stejnou transformaci (snapshot vlastních rectů). Dialog
      // v ploše (bez stackId) → jen on. Do localStorage se ale zapíše jen MĚNĚNÉ (vrchní) okno;
      // do kotvy jdou rectů všech automaticky (hash serializuje každé okno se svým rectem).
      const memberIds = layer?.stackId
        ? kernel
            .getState()
            .layers.filter((l) => l.stackId === layer.stackId)
            .map((l) => l.layerId)
        : [layerId];
      const snapshot = memberIds
        .map((id) => ({ id, rect: store.windows[id]?.rect }))
        .filter((m): m is { id: string; rect: WindowRect } => m.rect !== undefined);
      if (snapshot.length === 0) return;

      const startX = e.clientX;
      const startY = e.clientY;
      let raf = 0;
      document.body.style.userSelect = 'none';

      const resizeRect = (rect: WindowRect, dx: number, dy: number): WindowRect => {
        let { x, y, w, h } = rect;
        if (dir.includes('e')) w = Math.max(MIN_W, rect.w + dx);
        if (dir.includes('s')) h = Math.max(MIN_H, rect.h + dy);
        if (dir.includes('w')) {
          w = Math.max(MIN_W, rect.w - dx);
          x = rect.x + (rect.w - w);
        }
        if (dir.includes('n')) {
          h = Math.max(MIN_H, rect.h - dy);
          y = rect.y + (rect.h - h);
        }
        return { x, y, w, h };
      };

      beginDragCapture(e.pointerId, `${dir}-resize`, {
        onMove: (ev) => {
          const dx = ev.clientX - startX;
          const dy = ev.clientY - startY;
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = 0;
            wm.getState().setRects(snapshot.map(({ id, rect }) => [id, resizeRect(rect, dx, dy)]));
          });
        },
        onEnd: () => {
          document.body.style.userSelect = '';
          if (raf) cancelAnimationFrame(raf);
          const s = wm.getState();
          const dim = s.desktopSize;
          // Po resize smí okno přesahovat ven, ale drž viditelný titulek (clampKeepTitle) — pro
          // všechny členy stacku.
          const fixes = snapshot
            .map(({ id }) => {
              const cur = s.windows[id]?.rect;
              return cur ? ([id, clampRectKeepTitle(cur, dim)] as const) : null;
            })
            .filter((x): x is readonly [string, WindowRect] => x !== null);
          if (fixes.length > 0) s.setRects(fixes);
          // Zapamatuj velikost (NE pozici) do localStorage per agenda — jen pro MĚNĚNÉ (vrchní) okno.
          const layerNow = kernel.getLayer(layerId);
          const curTop = s.windows[layerId]?.rect;
          if (layerNow && curTop) {
            const clampedTop = clampRectKeepTitle(curTop, dim);
            writeWindowSizeMemory(layerNow, {
              w: clampedTop.w,
              h: clampedTop.h,
              maximized: false,
            });
          }
        },
      });
    },
    [kernel, wm]
  );

  return { startDrag, startResize };
}
